import React, { useState, useEffect } from "react";
import axios from "../api/axiosInstance";
import toast from "react-hot-toast";
import {
  FaCloudUploadAlt,
  FaImage,
  FaVideo,
  FaTimes,
  FaNewspaper,
} from "react-icons/fa";

const UploadBlogForm = () => {
  const [form, setForm] = useState({
    headline: "",
    description: "",
    media: null,
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mediaType, setMediaType] = useState(null); // "image" | "video"
  const [submitting, setSubmitting] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Clean up object URLs on unmount / change
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const setMediaFile = (file) => {
    if (!file) return;

    if (file.type.startsWith("video") && file.size > 50 * 1024 * 1024) {
      toast.error("Video size cannot exceed 50 MB!");
      return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);

    setForm((prev) => ({ ...prev, media: file }));
    setPreviewUrl(URL.createObjectURL(file));
    setMediaType(file.type.startsWith("video") ? "video" : "image");
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setMediaFile(files[0]);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setMediaFile(file);
  };

  const removeMedia = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setForm((prev) => ({ ...prev, media: null }));
    setPreviewUrl(null);
    setMediaType(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.headline.trim()) {
      toast.error("Headline is required");
      return;
    }

    const formData = new FormData();
    if (form.headline) formData.append("headline", form.headline);
    if (form.description) formData.append("description", form.description);
    if (form.media) formData.append("media", form.media);

    try {
      setSubmitting(true);
      await axios.post("/api/upload-story/", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Story uploaded successfully!");
      setForm({ headline: "", description: "", media: null });
      removeMedia();
    } catch (error) {
      console.error("Error:", error.response?.data || error.message);
      toast.error("Upload failed!");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-3 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
            <FaNewspaper size={16} />
          </span>
          <div>
            <h1 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl">
              Upload Story
            </h1>
            <p className="text-sm text-slate-500">
              Add a new headline, description and media for the blog feed
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-8">
          <form onSubmit={handleSubmit} className="grid gap-8 lg:grid-cols-5">
            {/* Left: form fields */}
            <div className="space-y-5 lg:col-span-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Headline <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="headline"
                  value={form.headline}
                  onChange={handleChange}
                  placeholder="Enter a clear, attention-grabbing headline"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-emerald-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Description
                </label>
                <textarea
                  name="description"
                  rows={6}
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Write a short summary for this story..."
                  className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 transition focus:border-emerald-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald-100"
                />
                <p className="mt-1 text-right text-xs text-slate-400">
                  {form.description.length} characters
                </p>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-600 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {submitting ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaCloudUploadAlt size={16} />
                    Upload Story
                  </>
                )}
              </button>
            </div>

            {/* Right: media upload + preview */}
            <div className="lg:col-span-2">
              <label className="mb-1.5 block text-sm font-medium text-slate-700">
                Image or video
              </label>

              {!previewUrl ? (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  className={`relative flex h-56 flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 text-center transition ${
                    dragActive
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-slate-200 bg-slate-50 hover:border-emerald-300 hover:bg-emerald-50/50"
                  }`}
                >
                  <FaCloudUploadAlt
                    size={28}
                    className={
                      dragActive ? "text-emerald-500" : "text-slate-400"
                    }
                  />
                  <p className="text-sm font-medium text-slate-600">
                    Drag and drop, or click to browse
                  </p>
                  <p className="text-xs text-slate-400">
                    Images or videos up to 50 MB
                  </p>
                  <input
                    type="file"
                    name="media"
                    accept="image/*,video/*"
                    onChange={handleChange}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                  />
                </div>
              ) : (
                <div className="relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                  <button
                    type="button"
                    onClick={removeMedia}
                    aria-label="Remove media"
                    className="absolute right-2 top-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-slate-900/70 text-white transition hover:bg-red-600"
                  >
                    <FaTimes size={13} />
                  </button>

                  {mediaType === "video" ? (
                    <video
                      src={previewUrl}
                      controls
                      className="h-56 w-full object-cover"
                    />
                  ) : (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="h-56 w-full object-cover"
                    />
                  )}

                  <div className="flex items-center gap-2 border-t border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                    {mediaType === "video" ? (
                      <FaVideo size={12} className="text-emerald-600" />
                    ) : (
                      <FaImage size={12} className="text-emerald-600" />
                    )}
                    <span className="truncate">{form.media?.name}</span>
                    <span className="ml-auto shrink-0">
                      {(form.media?.size / (1024 * 1024)).toFixed(1)} MB
                    </span>
                  </div>
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UploadBlogForm;
